import { useEffect, useMemo, useState } from 'react';
import { Alert, Flex, Modal, Typography, message } from 'antd';
import Editor from '@monaco-editor/react';

import {
	VSCPartData,
	VSCPartJson,
} from '../../../interfaces/VersaBuilt.Interface';
import { useUpdateVSCPartDataMutation } from '../../../redux/api/versabuilt';
import { extractApiError } from '../../../utils/apiError';
import UnitSwitch from './UnitSwitch';
import { jsonToInches, jsonToMetric } from './units';

interface RobotProgramEditModalProps {
	orderLineItemId: string;
	program: VSCPartData | null;
	useInches: boolean;
	onChangeUnits: (useInches: boolean) => void;
	onClose: () => void;
}

const stringify = (json: VSCPartJson) => JSON.stringify(json, null, 2);

const RobotProgramEditModal = ({
	orderLineItemId,
	program,
	useInches,
	onChangeUnits,
	onClose,
}: RobotProgramEditModalProps) => {
	const [messageApi, messageContext] = message.useMessage();
	const [value, setValue] = useState('');
	// The last metric version of the program; converting back from inches
	// compares against it so untouched lengths keep their exact stored value.
	const [metricBaseline, setMetricBaseline] = useState<VSCPartJson>({});
	const [parseError, setParseError] = useState<string | null>(null);
	const [updateVSCPartData, { isLoading }] = useUpdateVSCPartDataMutation();

	useEffect(() => {
		if (program) {
			setMetricBaseline(program.json_data);
			setValue(
				stringify(
					useInches ? jsonToInches(program.json_data) : program.json_data
				)
			);
			setParseError(null);
		}
	}, [program]);

	const toMetric = (edited: VSCPartJson) =>
		useInches ? jsonToMetric(edited, metricBaseline) : edited;

	// Compared as metric JSON, so reformatting or flipping units isn't a change.
	const hasChanges = useMemo(() => {
		if (!program || parseError) {
			return false;
		}
		try {
			return (
				JSON.stringify(toMetric(JSON.parse(value))) !==
				JSON.stringify(program.json_data)
			);
		} catch {
			return false;
		}
	}, [program, value, parseError, useInches, metricBaseline]);

	const handleUnitsChange = (nextUseInches: boolean) => {
		const metric = toMetric(JSON.parse(value));
		setMetricBaseline(metric);
		setValue(stringify(nextUseInches ? jsonToInches(metric) : metric));
		onChangeUnits(nextUseInches);
	};

	const handleChange = (next?: string) => {
		const text = next ?? '';
		setValue(text);
		try {
			const parsed = JSON.parse(text);
			setParseError(
				parsed && typeof parsed === 'object' && !Array.isArray(parsed)
					? null
					: 'The program must be a JSON object.'
			);
		} catch (e) {
			setParseError((e as Error).message);
		}
	};

	const handleSave = async () => {
		if (!program || parseError || !hasChanges) {
			return;
		}
		try {
			await updateVSCPartData({
				id: program.id,
				orderLineItemId,
				json_data: toMetric(JSON.parse(value)),
			}).unwrap();
			messageApi.success('Robot program saved');
			onClose();
		} catch (e) {
			messageApi.error(extractApiError(e));
		}
	};

	return (
		<>
			<Modal
				title={`Edit Robot Program${
					program?.json_data?.name ? ` — ${program.json_data.name}` : ''
				}`}
				open={!!program}
				onCancel={onClose}
				onOk={handleSave}
				okText="Save"
				okButtonProps={{
					disabled: !!parseError || !hasChanges,
					loading: isLoading,
				}}
				cancelButtonProps={{ disabled: isLoading }}
				maskClosable={false}
				destroyOnClose
				width={960}
				centered
			>
				<Flex justify="space-between" align="center" className="mb-3">
					<Typography.Text type="secondary">
						{useInches
							? 'Lengths are shown in inches and saved back in metric.'
							: 'Lengths are shown as stored, in metric.'}
					</Typography.Text>
					<UnitSwitch
						useInches={useInches}
						onChange={handleUnitsChange}
						disabled={!!parseError}
						disabledReason="Fix the JSON before switching units"
					/>
				</Flex>
				{parseError && (
					<Alert
						type="error"
						showIcon
						message="Invalid JSON"
						description={parseError}
						className="mb-3"
					/>
				)}
				<div className="border">
					<Editor
						height="65vh"
						defaultLanguage="json"
						value={value}
						onChange={handleChange}
						options={{
							minimap: { enabled: false },
							scrollBeyondLastLine: false,
							tabSize: 2,
							formatOnPaste: true,
							automaticLayout: true,
						}}
					/>
				</div>
			</Modal>
			{messageContext}
		</>
	);
};

export default RobotProgramEditModal;
